import { MINIMUM_SCORE, DAY_NAMES, DAY_ICONS } from '../utils/constants.js';
import { formatCompact } from '../utils/formatting.js';

/**
 * Check if a member was active during an event using multiple periods
 * @param {array} memberPeriods - Array of { start_date, end_date } objects
 * @param {Date} eventStartDate - Start of event
 * @param {Date} eventEndDate - End of event
 * @returns {boolean} True if member was active during event
 */
export const isMemberActiveInEvent = (memberPeriods, eventStartDate, eventEndDate) => {
  if (!memberPeriods || memberPeriods.length === 0) {
    // No periods means we can't determine activity, so allow it (backwards compatible)
    return true;
  }

  const eventStart = new Date(eventStartDate);
  const eventEnd = new Date(eventEndDate);

  // Check if any period overlaps with the event date range
  return memberPeriods.some(period => {
    const periodStart = new Date(period.start_date);
    const periodEnd = period.end_date ? new Date(period.end_date) : new Date(); // If no end_date, member is still active

    // Check for overlap: period starts before event ends AND period ends after event starts
    return periodStart <= eventEnd && periodEnd >= eventStart;
  });
};

/**
 * Get the active period for a member at a specific date
 * @param {array} memberPeriods - Array of member periods
 * @param {Date} targetDate - Date to check
 * @returns {object|null} The active period or null
 */
export const getActivePeriodAtDate = (memberPeriods, targetDate) => {
  if (!memberPeriods || memberPeriods.length === 0) return null;

  const target = new Date(targetDate);
  
  return memberPeriods.find(period => {
    const start = new Date(period.start_date);
    const end = period.end_date ? new Date(period.end_date) : new Date();
    
    return start <= target && target <= end;
  }) || null;
};

/**
 * Calculate total scores for guild and opponent
 * @param {object} selectedEvent - Currently selected event
 * @param {array} memberContributions - All member contributions
 * @param {array} dailyScores - Daily scores data
 * @returns {object} Object with yourTotal, opponentTotal, guildDailyTotals
 */
export const calculateTotals = (selectedEvent, memberContributions, dailyScores) => {
  if (!selectedEvent) return { yourTotal: 0, opponentTotal: 0, guildDailyTotals: Array(6).fill(0) };
  
  const guildDailyTotals = Array(6).fill(0);
  memberContributions.forEach(c => {
    if (c.day_number >= 1 && c.day_number <= 6) {
      guildDailyTotals[c.day_number - 1] += (Number(c.score) || 0);
    }
  });
  
  const yourTotal = guildDailyTotals.reduce((s, v) => s + v, 0);
  const opponentTotal = dailyScores.reduce((s, d) => s + (d.opponent_score || 0), 0);
  
  return { yourTotal, opponentTotal, guildDailyTotals };
};

/**
 * Calculate win/loss/draw results for each day
 * @param {array} dailyScores - Daily scores data
 * @returns {array} Array of results per day ('won', 'lost', 'draw')
 */
export const getDayResults = (dailyScores) => {
  const results = Array(6).fill('draw');
  dailyScores.forEach(d => {
    if (d.day_number >= 1 && d.day_number <= 6) {
      const idx = d.day_number - 1;
      const guildScore = (d.your_guild_score || 0);
      const oppScore = (d.opponent_score || 0);
      results[idx] = guildScore > oppScore ? 'won' : guildScore < oppScore ? 'lost' : 'draw';
    }
  });
  return results;
};

/**
 * Get day results with day number
 * @param {array} dailyScores - Daily scores data
 * @returns {array} Array of objects with day and result
 */
export const getDaysDisplay = (dailyScores) => {
  const results = getDayResults(dailyScores);
  return results.map((result, idx) => ({
    day: idx + 1,
    result
  }));
};

/**
 * Calculate member rankings for an event
 * @param {object} selectedEvent - Currently selected event
 * @param {array} members - All members
 * @param {array} memberContributions - All contributions
 * @param {boolean} filterLowPerformers - Filter to show only low performers
 * @param {boolean} filterActiveOnly - Filter to show only active members
 * @param {string} searchQueryRankings - Search query
 * @param {string} sortBy - Sort field ('total' or 'day1'-'day6')
 * @returns {array} Sorted member rankings
 */
export const getMemberRankings = (
  selectedEvent,
  members,
  memberContributions,
  filterLowPerformers = false,
  filterActiveOnly = true,
  searchQueryRankings = '',
  sortBy = 'total'
) => {
  if (!selectedEvent || members.length === 0) return [];
  
  const memberScores = {};
  members.forEach(m => {
    memberScores[m.id] = {
      id: m.id,
      name: m.display_name || m.name,
      status: m.is_active === false ? 'inactive' : 'active',
      isAlt: m.is_alt,
      scores: Array(6).fill(0),
      total: 0,
      lowPerformanceDays: 0,
      joinedDate: m.joined_date,
      leftDate: m.left_date
    };
  });
  
  memberContributions.forEach(c => {
    if (memberScores[c.member_id] && c.day_number >= 1 && c.day_number <= 6) {
      memberScores[c.member_id].scores[c.day_number - 1] = Number(c.score) || 0;
    }
  });
  
  let rankings = Object.values(memberScores).map(m => {
    m.total = m.scores.reduce((s, v) => s + v, 0);
    m.lowPerformanceDays = m.scores.filter(s => s > 0 && s < MINIMUM_SCORE).length;
    return m;
  });
  
  // Filter low performers
  if (filterLowPerformers) {
    rankings = rankings.filter(r => r.lowPerformanceDays > 0 || r.total === 0);
  }
  
  // Filter active members
  if (filterActiveOnly && selectedEvent) {
    const eventDate = new Date(selectedEvent.event_date);
    const eventEndDate = selectedEvent.end_date
      ? new Date(selectedEvent.end_date)
      : new Date(eventDate.getTime() + 6 * 24 * 60 * 60 * 1000);
    
    rankings = rankings.filter(r => {
      const member = members.find(m => (m.display_name || m.name) === r.name);
      if (!member || !member.joined_date) return true;
      
      const joinedDate = new Date(member.joined_date);
      if (joinedDate > eventEndDate) return false;
      if (member.left_date && new Date(member.left_date) < eventDate) return false;
      
      return true;
    });
  }
  
  // Search filter
  if (searchQueryRankings.trim()) {
    const q = searchQueryRankings.toLowerCase();
    rankings = rankings.filter(r => r.name.toLowerCase().includes(q));
  }
  
  // Sort
  rankings.sort((a, b) => {
    if (sortBy === 'total') return b.total - a.total;
    if (sortBy.startsWith('day')) {
      const i = parseInt(sortBy.replace('day', '')) - 1;
      return b.scores[i] - a.scores[i];
    }
    return 0;
  });
  
  return rankings;
};

/**
 * Get statistics per day across all events
 * @param {array} memberContributions - All contributions
 * @param {array} events - All events
 * @param {object} selectedEvent - Currently selected event
 * @returns {array} Statistics for each day
 */
export const getStatsPerDay = (memberContributions, events, selectedEvent) => {
  let contribsToUse = memberContributions;
  
  // Handle "All Events" selection
  if (selectedEvent?.id === 'ALL') {
    if (memberContributions.length === 0 && events.length > 0) {
      return Array(6).fill(null).map((_, i) => ({
        dayNumber: i + 1,
        dayName: DAY_NAMES[i],
        dayIcon: DAY_ICONS[i],
        scores: [],
        median: 0,
        p10: 0,
        p90: 0,
        min: 0,
        max: 0,
        guildTotals: []
      }));
    }
  }
  
  if (events.length === 0 || !contribsToUse || contribsToUse.length === 0) {
    return Array(6).fill(null).map((_, i) => ({
      dayNumber: i + 1,
      dayName: DAY_NAMES[i],
      dayIcon: DAY_ICONS[i],
      scores: [],
      median: 0,
      p10: 0,
      p90: 0,
      min: 0,
      max: 0,
      guildTotals: []
    }));
  }
  
  const statsByDay = Array(6).fill(null).map(() => ({ scores: [], guildTotals: [] }));
  
  // Group contributions by day
  events.forEach(event => {
    const eventContribs = contribsToUse.filter(c => c.event_id === event.id);
    const guildDailyTotals = Array(6).fill(0);
    
    eventContribs.forEach(c => {
      if (c.day_number >= 1 && c.day_number <= 6) {
        const dayIdx = c.day_number - 1;
        statsByDay[dayIdx].scores.push(Number(c.score) || 0);
        guildDailyTotals[dayIdx] += (Number(c.score) || 0);
      }
    });
    
    guildDailyTotals.forEach((total, idx) => {
      if (total > 0) statsByDay[idx].guildTotals.push(total);
    });
  });
  
  // Calculate percentiles
  const calculatePercentile = (arr, p) => {
    if (arr.length === 0) return 0;
    const sorted = [...arr].sort((a, b) => a - b);
    const idx = Math.ceil((p / 100) * sorted.length) - 1;
    return sorted[Math.max(0, idx)];
  };
  
  return statsByDay.map((dayStats, idx) => {
    const scores = dayStats.scores.filter(s => s > 0);
    const guildTotals = dayStats.guildTotals;
    
    const sorted = [...scores].sort((a, b) => a - b);
    const median = sorted.length > 0 ? sorted[Math.floor(sorted.length / 2)] : 0;
    const p10 = calculatePercentile([...scores], 10);
    const p90 = calculatePercentile([...scores], 90);
    const min = scores.length > 0 ? Math.min(...scores) : 0;
    const max = scores.length > 0 ? Math.max(...scores) : 0;
    
    return {
      dayNumber: idx + 1,
      dayName: DAY_NAMES[idx],
      dayIcon: DAY_ICONS[idx],
      scores,
      median,
      p10,
      p90,
      min,
      max,
      guildTotals
    };
  });
};

/**
 * Get member's total score across all events
 * @param {string} memberId - Member ID
 * @param {array} memberContributions - All contributions
 * @returns {number} Total score
 */
export const getMemberAllEventsTotal = (memberId, memberContributions) => {
  return memberContributions
    .filter(c => c.member_id === memberId)
    .reduce((sum, c) => sum + (Number(c.score) || 0), 0);
};

/**
 * Get member's average score per event
 * @param {string} memberId - Member ID
 * @param {array} memberContributions - All contributions
 * @returns {number} Average score per event
 */
export const getMemberAveragePerEvent = (memberId, memberContributions) => {
  const allScores = memberContributions.filter(c => c.member_id === memberId);
  if (allScores.length === 0) return 0;
  const total = allScores.reduce((sum, c) => sum + (Number(c.score) || 0), 0);
  return total / allScores.length;
};

/**
 * Get top 10 individual scores across all events by day type
 * @param {array} memberContributions - All contributions
 * @param {array} members - All members
 * @returns {array} Top 10 scores per day
 */
export const getTopScoresAllEventsPerDay = (memberContributions, members) => {
  const topByDay = Array(6).fill(null).map(() => ({}));
  
  memberContributions.forEach(c => {
    if (c.day_number >= 1 && c.day_number <= 6 && c.score > 0) {
      const dayIndex = c.day_number - 1;
      const memberObj = members.find(m => m.id === c.member_id);
      const memberName = memberObj?.display_name || memberObj?.name || 'Unknown';
      const scoreKey = `${memberName}_${c.score}`;
      
      if (!topByDay[dayIndex][scoreKey]) {
        topByDay[dayIndex][scoreKey] = { name: memberName, score: c.score };
      }
    }
  });
  
  return topByDay.map(dayScores => {
    return Object.values(dayScores)
      .sort((a, b) => b.score - a.score)
      .slice(0, 10);
  });
};

/**
 * Calculate daily medals (gold/silver/bronze for top 3)
 * @param {array} rankingsData - Member rankings
 * @returns {object} Medals per member
 */
export const getDailyMedals = (rankingsData) => {
  const medals = {};
  
  for (let dayIndex = 0; dayIndex < 6; dayIndex++) {
    const dayScores = rankingsData
      .map(r => ({ name: r.name, score: r.scores[dayIndex] }))
      .filter(x => x.score > 0)
      .sort((a, b) => b.score - a.score);
    
    if (dayScores[0]) {
      medals[dayScores[0].name] = medals[dayScores[0].name] || { gold: 0, silver: 0, bronze: 0 };
      medals[dayScores[0].name].gold++;
    }
    if (dayScores[1]) {
      medals[dayScores[1].name] = medals[dayScores[1].name] || { gold: 0, silver: 0, bronze: 0 };
      medals[dayScores[1].name].silver++;
    }
    if (dayScores[2]) {
      medals[dayScores[2].name] = medals[dayScores[2].name] || { gold: 0, silver: 0, bronze: 0 };
      medals[dayScores[2].name].bronze++;
    }
  }
  
  return medals;
};

/**
 * Calculate medals across all events
 * @param {array} events - All events
 * @param {array} memberContributions - All contributions
 * @param {array} members - All members
 * @returns {object} Medals per member
 */
export const getDailyMedalsAllEvents = (events, memberContributions, members) => {
  const medals = {};
  const allEventsList = events.filter(e => e.id !== 'ALL');
  
  allEventsList.forEach(event => {
    const eventContribs = memberContributions.filter(c => c.event_id === event.id);
    
    for (let dayIndex = 1; dayIndex <= 6; dayIndex++) {
      const dayScores = eventContribs
        .filter(c => c.day_number === dayIndex && c.score > 0)
        .reduce((acc, c) => {
          const member = members.find(m => m.id === c.member_id);
          const memberName = member?.display_name || member?.name || 'Unknown';
          const existing = acc.find(x => x.name === memberName);
          if (existing) {
            existing.score = Math.max(existing.score, c.score);
          } else {
            acc.push({ name: memberName, score: c.score });
          }
          return acc;
        }, [])
        .sort((a, b) => b.score - a.score);
      
      if (dayScores[0]) {
        medals[dayScores[0].name] = medals[dayScores[0].name] || { gold: 0, silver: 0, bronze: 0 };
        medals[dayScores[0].name].gold++;
      }
      if (dayScores[1]) {
        medals[dayScores[1].name] = medals[dayScores[1].name] || { gold: 0, silver: 0, bronze: 0 };
        medals[dayScores[1].name].silver++;
      }
      if (dayScores[2]) {
        medals[dayScores[2].name] = medals[dayScores[2].name] || { gold: 0, silver: 0, bronze: 0 };
        medals[dayScores[2].name].bronze++;
      }
    }
  });
  
  return medals;
};

/**
 * Get cumulative medals for a member
 * @param {string} memberName - Member name
 * @param {array} memberContributions - Member's contributions
 * @returns {object} Cumulative medals
 */
export const getCumulativeMedalsForMember = (memberName, memberContributions) => {
  const medals = { gold: 0, silver: 0, bronze: 0 };
  if (!memberContributions || memberContributions.length === 0) return medals;
  
  // This is a simplified version - full calculation would require full event data
  memberContributions.forEach(eventData => {
    if (eventData.scores && eventData.event) {
      eventData.scores.forEach((score, dayIdx) => {
        if (score > 0) {
          // Count high scores as potential medals
          // Full calculation requires comparing against all members
        }
      });
    }
  });
  
  return medals;
};
